import { Component } from '@theme/component';
import { ThemeEvents, StringingUpdateEvent } from '@theme/events';
import {
  readCustomVariantMap,
  findCustomCounterpart,
  normalizeVariantId,
  resolveCustomSubmission,
  remainingStock,
} from '@theme/custom-variant-map';

/**
 * @typedef {object} CustomBatsRefs
 * @property {HTMLInputElement} enableCheckbox
 * @property {HTMLElement} configPanel
 * @property {HTMLInputElement} textInput
 * @property {HTMLElement} fontOptions
 * @property {HTMLElement} colorOptions
 * @property {HTMLElement} selectedColorName
 * @property {HTMLInputElement} propertyText
 * @property {HTMLInputElement} propertyFont
 * @property {HTMLInputElement} propertyColor
 * @property {{ showDialog: () => void } & HTMLElement} examplesModal
 */

/**
 * @extends {Component<CustomBatsRefs>}
 */
class CustomBatsPersonalization extends Component {
  /** @type {boolean} */
  #enabled = false;

  connectedCallback() {
    super.connectedCallback();

    const enableCheckbox = this.refs.enableCheckbox;
    if (enableCheckbox instanceof HTMLInputElement) {
      this.#enabled = enableCheckbox.checked;
    }

    this.#bindToggleAccessibility();
    this.#syncControlsEnabled();
    this.#syncPropertyFields();

    this.closest('.shopify-section, dialog')?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    // The browser can restore a checked box on back navigation: show the custom price for it.
    if (this.#enabled) queueMicrotask(() => this.#dispatchPriceUpdate());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.closest('.shopify-section, dialog')?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * Custom bats (WIL26D2C-623): the hidden Customization "With" variant carries the full
   * custom price. The page stays on the stock variant so OOS states follow stock; product-form
   * submits the "With" counterpart instead. Bats without that option keep the old behaviour.
   *
   * @param {string} [stockVariantId] - The variant id product-form is submitting. Preferred over the
   *   last variantUpdate, which can lag behind a queued add to cart.
   * @returns {{ enabled: true, complete: boolean, hasCustomVariants: boolean, customVariantId: string, stockSku: string, stockAvailable: boolean, unavailableMessage: string } | null}
   */
  getCartSubmission(stockVariantId) {
    if (!this.#enabled) return null;

    this.#syncPropertyFields();
    const { propertyText, propertyFont, propertyColor } = this.refs;
    // Checkout validation requires all three on a custom bat line.
    const complete = [propertyText, propertyFont, propertyColor].every(
      (input) => input instanceof HTMLInputElement && input.value.trim() !== ''
    );

    const map = this.#getVariantMap();
    const { customVariantId, stockSku, stockAvailable } = resolveCustomSubmission(
      map,
      normalizeVariantId(stockVariantId) || this.#getStockVariantId()
    );
    return {
      enabled: true,
      complete,
      hasCustomVariants: map != null,
      customVariantId,
      stockSku,
      stockAvailable,
      unavailableMessage: this.dataset.unavailableMessage || '',
    };
  }

  /**
   * Stock units still free for this size, given the cart (stock and custom lines share them).
   * Null when this bat has no Customization option or the stock variant isn't limited.
   *
   * @param {string} stockVariantId
   * @param {Array<{ variant_id: string|number, quantity: number }>} cartItems
   * @returns {number | null}
   */
  getRemainingStock(stockVariantId, cartItems) {
    return remainingStock(this.#getVariantMap(), normalizeVariantId(stockVariantId), cartItems);
  }

  /** @returns {string} */
  get stockLimitMessage() {
    return this.dataset.stockLimitMessage || '';
  }

  /** @param {import('@theme/events').VariantUpdateEvent} event */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const variantId = event.detail.resource?.id;
    if (variantId != null) this.dataset.baseVariantId = String(variantId);

    // After product-price / product-form have replaced their markup for the new variant.
    queueMicrotask(() => this.#dispatchPriceUpdate());
  };

  /**
   * Shows the price of the variant add to cart will buy: the "With" counterpart while
   * personalization is checked, otherwise the stock variant. Prices come from the market
   * catalog via the Liquid variant map. No-op for bats without the Customization option.
   */
  #dispatchPriceUpdate() {
    if (!this.#getVariantMap()) return;

    const stockEntry = this.#getStockEntry();
    const target = this.#enabled ? this.#getCounterpart() || stockEntry : stockEntry;
    if (!target || typeof target.price !== 'number') return;

    this.dispatchEvent(
      new StringingUpdateEvent({
        enabled: this.#enabled,
        baseVariantPrice: target.price,
        stringAddOnPrice: 0,
        totalPrice: target.price,
        compareAtPrice: Number(target.compareAtPrice) || 0,
        variantId: normalizeVariantId(target.id),
        moneyFormat: this.dataset.moneyFormat || '{{amount}}',
        currency: this.dataset.currency || '',
      })
    );
  }

  /** @returns {string} */
  #getStockVariantId() {
    if (this.dataset.baseVariantId) return normalizeVariantId(this.dataset.baseVariantId);

    const form = this.dataset.productFormId ? document.getElementById(this.dataset.productFormId) : null;
    const idInput = form?.querySelector('input[name="id"]');
    return idInput instanceof HTMLInputElement ? normalizeVariantId(idInput.value) : '';
  }

  #getVariantMap() {
    return readCustomVariantMap(this, '[data-custom-variants]');
  }

  #getStockEntry() {
    const stockVariantId = this.#getStockVariantId();
    return this.#getVariantMap()?.variants?.find((variant) => normalizeVariantId(variant.id) === stockVariantId) || null;
  }

  #getCounterpart() {
    return findCustomCounterpart(this.#getVariantMap(), this.#getStockVariantId());
  }

  #bindToggleAccessibility() {
    const enableCheckbox = this.refs.enableCheckbox;
    const { configPanel } = this.refs;

    if (enableCheckbox instanceof HTMLInputElement && configPanel?.id) {
      enableCheckbox.setAttribute('aria-controls', configPanel.id);
    }
  }

  /**
   * @param {Event} event
   */
  handleEnableChange(event) {
    const enableCheckbox = this.refs.enableCheckbox;
    const input =
      enableCheckbox instanceof HTMLInputElement
        ? enableCheckbox
        : event.target instanceof HTMLInputElement
          ? event.target
          : null;

    if (!input) return;

    this.#enabled = input.checked;
    this.#syncControlsEnabled();
    this.#syncPropertyFields();
    this.#dispatchPriceUpdate();
  }

  handleTextInput() {
    this.#syncPropertyFields();
  }

  handleFontSelect() {
    this.#syncPropertyFields();
  }

  /**
   * @param {Event} event
   */
  handleColorSelect(event) {
    const target = event.target;
    if (target instanceof HTMLInputElement && this.refs.selectedColorName) {
      this.refs.selectedColorName.textContent = target.dataset.colorName || '';
    }
    this.#syncPropertyFields();
  }

  openExamplesModal() {
    const modal = this.refs.examplesModal;
    if (modal && typeof (/** @type {any} */ (modal).showDialog) === 'function') {
      /** @type {any} */ (modal).showDialog();
    }
  }

  #syncControlsEnabled() {
    const { configPanel, textInput, fontOptions, colorOptions, enableCheckbox, selectedColorName } = this.refs;

    if (configPanel) {
      configPanel.classList.toggle('is-expanded', this.#enabled);
      configPanel.setAttribute('aria-hidden', this.#enabled ? 'false' : 'true');
      configPanel.toggleAttribute('inert', !this.#enabled);
    }

    if (enableCheckbox instanceof HTMLInputElement) {
      enableCheckbox.setAttribute('aria-expanded', this.#enabled ? 'true' : 'false');
    }

    if (textInput instanceof HTMLInputElement) {
      textInput.disabled = !this.#enabled;
    }

    if (fontOptions instanceof HTMLElement) {
      for (const input of fontOptions.querySelectorAll('.custom-bats__font-input')) {
        if (input instanceof HTMLInputElement) input.disabled = !this.#enabled;
      }
    }

    if (colorOptions instanceof HTMLElement) {
      for (const input of colorOptions.querySelectorAll('.custom-bats__color-input')) {
        if (input instanceof HTMLInputElement) input.disabled = !this.#enabled;
      }
    }

    if (!this.#enabled && selectedColorName instanceof HTMLElement) {
      selectedColorName.textContent = '';
    }
  }

  #syncPropertyFields() {
    const { propertyText, propertyFont, propertyColor, textInput, fontOptions, colorOptions } = this.refs;

    if (!this.#enabled) {
      for (const input of [propertyText, propertyFont, propertyColor]) {
        if (input instanceof HTMLInputElement) {
          input.value = '';
          input.disabled = true;
        }
      }
      return;
    }

    const selectedFont =
      fontOptions instanceof HTMLElement
        ? fontOptions.querySelector('.custom-bats__font-input:checked')
        : null;
    const selectedColor =
      colorOptions instanceof HTMLElement
        ? colorOptions.querySelector('.custom-bats__color-input:checked')
        : null;

    if (propertyText instanceof HTMLInputElement) {
      propertyText.value = textInput instanceof HTMLInputElement ? textInput.value : '';
      propertyText.disabled = false;
    }
    if (propertyFont instanceof HTMLInputElement) {
      propertyFont.value = selectedFont instanceof HTMLInputElement ? selectedFont.value : '';
      propertyFont.disabled = false;
    }
    if (propertyColor instanceof HTMLInputElement) {
      propertyColor.value = selectedColor instanceof HTMLInputElement ? selectedColor.value : '';
      propertyColor.disabled = false;
    }
  }
}

if (!customElements.get('custom-bats-personalization-component')) {
  customElements.define('custom-bats-personalization-component', CustomBatsPersonalization);
}

export { CustomBatsPersonalization };
